// Calendar days travel across the bridge as dates, never as UTC-midnight timestamps.
export function healthDay(value = new Date()) {
  return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`
}

export function validHealthDay(value) {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false
  const date = new Date(`${value}T12:00:00Z`)
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value
}

export function moveHealthDay(day, count) {
  if (!validHealthDay(day) || !Number.isInteger(count)) throw new Error("Ungültiges Datum")
  const date = new Date(`${day}T12:00:00Z`)
  date.setUTCDate(date.getUTCDate() + count)
  return date.toISOString().slice(0, 10)
}

export function healthPeriod(anchor, mode = "week") {
  if (!validHealthDay(anchor)) throw new Error("Ungültiges Datum")
  const date = new Date(`${anchor}T12:00:00Z`)
  if (mode === "month") {
    const from = `${anchor.slice(0, 7)}-01`
    date.setUTCMonth(date.getUTCMonth() + 1, 0)
    return { from, to: date.toISOString().slice(0, 10) }
  }
  const from = moveHealthDay(anchor, -((date.getUTCDay() + 6) % 7))
  return { from, to: moveHealthDay(from, 6) }
}

export function healthDays(from, to) {
  if (!validHealthDay(from) || !validHealthDay(to) || from > to) throw new Error("Ungültiger Zeitraum")
  const result = []
  for (let day = from; day <= to; day = moveHealthDay(day, 1)) {
    if (result.length === 31) throw new Error("Bitte höchstens einen Monat auswählen")
    result.push(day)
  }
  return result
}

export const HEALTH_METRICS = [
  { key: "steps", category: "steps", label: "Schritte", icon: "mdi-shoe-print", unit: "", color: "#a3c6c4", total: true },
  { key: "sleepSeconds", category: "sleep", label: "Schlaf", icon: "mdi-weather-night", unit: "", color: "#b4add7", total: false },
  { key: "totalEnergyKilocalories", category: "energy", label: "Kalorienverbrauch", icon: "mdi-fire", unit: "kcal", color: "#dfc7a7", total: true },
  { key: "activeEnergyKilocalories", category: "energy", label: "Aktive Kalorien", icon: "mdi-fire", unit: "kcal", color: "#f7b5a7", total: true },
  { key: "distanceMeters", category: "distance", label: "Distanz", icon: "mdi-map-marker-distance", unit: "km", color: "#99bed5", total: true },
  { key: "heartRate", category: "heartRate", label: "Puls", icon: "mdi-heart-pulse", unit: "BPM", color: "#dfaab7", total: false },
  { key: "weightKilograms", category: "weight", label: "Gewicht", icon: "mdi-scale-bathroom", unit: "kg", color: "#b8c9ad", total: false },
]

export function parseHealthHistory(raw, range) {
  const dates = healthDays(range.from, range.to)
  if (!raw || raw.from !== range.from || raw.to !== range.to || !Number.isFinite(raw.syncedAt) || !Array.isArray(raw.days)) throw new Error("Der Verlauf konnte nicht gelesen werden.")
  const known = new Map()
  for (const item of raw.days) {
    if (!item || !dates.includes(item.day) || known.has(item.day)) throw new Error("Der Verlauf enthält ungültige Tage.")
    const values = {}
    for (const metric of HEALTH_METRICS) {
      const value = item.values?.[metric.key]
      if (typeof value === "number" && Number.isFinite(value) && value >= 0) values[metric.key] = value
    }
    const readable = item.readable === true && item.future !== true
    known.set(item.day, { day: item.day, values: readable ? values : {}, readable, future: item.future === true, partial: item.partial === true })
  }
  return { provider: raw.provider === "apple_health" ? "apple_health" : "health_connect", from: raw.from, to: raw.to, syncedAt: raw.syncedAt, timeZone: typeof raw.timeZone === "string" ? raw.timeZone : "",
    limited: raw.limited === true,
    days: dates.map(day => known.get(day) || { day, values: {}, readable: false, future: false, partial: false }) }
}

export function healthMetricValue(key, value) {
  if (!Number.isFinite(value) || value < 0) return "–"
  if (key === "sleepSeconds") {
    const minutes = Math.round(value / 60)
    return `${Math.floor(minutes / 60)} h ${String(minutes % 60).padStart(2, "0")}`
  }
  const decimals = ["distanceMeters", "weightKilograms"].includes(key) ? 1 : 0
  return (key === "distanceMeters" ? value / 1000 : value).toLocaleString("de-CH", { maximumFractionDigits: decimals })
}

export function healthMetricSummary(days, key) {
  // A missing measurement is not zero. Today's incomplete activity never lowers the daily average.
  const recorded = days.filter(day => day.readable && !day.future && Number.isFinite(day.values[key]))
  const complete = recorded.filter(day => !day.partial)
  const values = complete.map(day => day.values[key])
  return { count: complete.length, recordedCount: recorded.length,
    expected: days.filter(day => day.readable && !day.future && !day.partial).length,
    average: values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : null,
    total: recorded.length ? recorded.reduce((sum, day) => sum + day.values[key], 0) : null,
    maximum: values.length ? Math.max(...values) : null,
    minimum: values.length ? Math.min(...values) : null }
}
